import { memo } from 'react';
import { DateTime } from 'luxon';
import { FormattedMessage } from 'react-intl';
import { HStack } from '../../layout';
import notEmpty from '../../../../core/util/notEmpty';
import { Button } from '../../buttons';
import { Text } from '../../typography';
import { CloseIcon } from '../../icons';
import { Pill } from '../../content';
import { isDateRangeValue } from './types';
import type { FilterValue, CommonFiltrationProps } from './types';

const FiltersValues = <T,>({
    filters,
    filterValue,
    emptyValue,
    onChangeFilters,
}: Pick<
    CommonFiltrationProps<T>,
    'filters' | 'filterValue' | 'onChangeFilters' | 'emptyValue'
>) => {
    const values: FilterValue[] = filters
        .flatMap(filter => {
            const currentValue = filterValue[filter.field];
            if (!currentValue) {
                return null;
            }
            switch (filter.type) {
                case 'search': {
                    return null;
                }
                case 'select': {
                    return [
                        {
                            label:
                                filter.items.find(
                                    item => item.value === currentValue
                                )?.label || (currentValue as string),
                            onRemove: () => {
                                onChangeFilters({
                                    ...filterValue,
                                    [filter.field]: undefined,
                                });
                            },
                        },
                    ];
                }
                case 'multiSelect': {
                    return (currentValue as string[]).map(value => {
                        return {
                            label:
                                filter.items.find(item => item.value === value)
                                    ?.label || value,
                            onRemove: () => {
                                onChangeFilters({
                                    ...filterValue,
                                    [filter.field]: (
                                        currentValue as string[]
                                    ).filter(item => item !== value),
                                });
                            },
                        };
                    });
                }
                case 'dateRange': {
                    const startValue = isDateRangeValue(currentValue)
                        ? currentValue.startDate
                        : undefined;
                    const endValue = isDateRangeValue(currentValue)
                        ? currentValue.endDate
                        : undefined;
                    if (!startValue && !endValue) {
                        return null;
                    }
                    return [
                        {
                            label: [startValue, endValue]
                                .filter(notEmpty)
                                .map(date =>
                                    DateTime.fromISO(date).toFormat(
                                        'dd.LL.yyyy'
                                    )
                                )
                                .join(' - '),
                            onRemove: () => {
                                onChangeFilters({
                                    ...filterValue,
                                    date: undefined,
                                });
                            },
                        },
                    ];
                }
                default: {
                    return null;
                }
            }
        })
        .filter(notEmpty);

    if (values.length === 0) {
        return null;
    }
    return (
        <HStack align="center" wrap gap="mini">
            <Text>
                <FormattedMessage id="Library Filtration SelectedFilters" />
            </Text>
            <Button
                outline
                rounded
                danger
                mini
                onClick={() => onChangeFilters(emptyValue)}
                data-test-class="filtration-clear"
            >
                <HStack align="center" gap="mini">
                    <Text secondary lineHeightAsFontSize>
                        <FormattedMessage id="Library Filtration ClearAll" />
                    </Text>
                    <CloseIcon mini />
                </HStack>
            </Button>
            {values.map(value => {
                return (
                    <Pill key={value.label} onRemove={value.onRemove}>
                        <Text
                            data-test-state={`pill-${value.label}`}
                            secondary
                            lineHeightAsFontSize
                        >
                            {value.label}
                        </Text>
                    </Pill>
                );
            })}
        </HStack>
    );
};

// eslint-disable-next-line @hostettler/memoize-component
export default memo(FiltersValues) as unknown as typeof FiltersValues;
